/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { task } from 'ember-concurrency';
import { computed } from '@ember/object';
import DS from 'ember-data';
import { inject as service } from '@ember/service';
import { ModelWithSubscriptionTopic } from 'cio/utils/subscription-center';
import SubscriptionCenterService from 'cio/services/subscription-center';
import EnvironmentService from 'cio/services/environment';
import { storageFor } from 'ember-local-storage';
import NewsletterModel from 'cio/models/newsletter';
import CampaignModel from 'cio/models/campaign';
import { equal } from '@ember/object/computed';
import { isNone } from '@ember/utils';
import ActionModel from 'cio/models/action';

interface AudienceSelectionArgs {
  model: ModelWithSubscriptionTopic;
  disabled?: boolean;
  onChange: (subscriptionTopicId: string | null, sendToUnsubscribed: boolean, useDefaultSettings?: boolean) => unknown;
  usageContext?: 'campaign' | 'broadcast' | 'action';
}

enum DefaultValues {
  OnlySubscribed = 'only_subscribed',
  SubAndUnsubscribed = 'all',
  CampaignSettings = 'use_campaign_settings',
}

enum Audiences {
  Global = 'Global',
  TopicBased = 'Topics',
}

const SHOW_NO_TOPICS_BANNER = 'show-no-sub-topics-banner';
const NO_TOPICS_BANNER_VIEWS = 'no-sub-topics-banner-views-count';

export default class AudienceSelection extends Component<AudienceSelectionArgs> {
  @service store!: DS.Store;
  @service subscriptionCenter!: SubscriptionCenterService;
  @service environment!: EnvironmentService;

  @storageFor('cio-alerts') localStorage!: any;
  @equal('args.usageContext', 'action') isUsedInAction!: boolean;

  @tracked tempDismissNoTopicBanner = this.showNoSubTopicsBanner;
  concatEnvID = `-env-${this.environment.current.id}`;

  get isNewsletter() {
    return this.args.model instanceof NewsletterModel;
  }

  get isAPITriggeredBroadcast() {
    return this.args.model instanceof CampaignModel && (this.args.model as CampaignModel).isApiTriggered;
  }

  get model() {
    if (this.isAPITriggeredBroadcast) {
      return 'broadcast';
    }
    if (this.args.model instanceof CampaignModel) {
      return 'campaign';
    }
    return 'newsletter';
  }

  get disabledBannerType() {
    if (this.isNewsletter) {
      return 'newsletter';
    } else if (this.isAPITriggeredBroadcast) {
      return 'broadcast';
    }

    return null;
  }

  optionOnlySubscribed = Object.freeze({ value: DefaultValues.OnlySubscribed, label: 'All subscribed people' });
  optionSubAndUnsubscribed = Object.freeze({
    value: DefaultValues.SubAndUnsubscribed,
    label: 'All subscribed and unsubscribed',
  });
  optionCampaignSetting = Object.freeze({ value: DefaultValues.CampaignSettings, label: 'Use campaign settings' });

  get audiencePlaceholder() {
    if (this.args.model.hasDeletedSubscriptionTopic === true) {
      return 'Topic deleted';
    }
    if (this.args.model.subscriptionTopicId != null) {
      if (!this.subscriptionCenter.allTopics.find((topic) => topic.id === this.args.model.subscriptionTopicId)) {
        return 'Topic deleted';
      }
    }
    return 'Select an audience…';
  }

  get defaultOptions() {
    if (this.subscriptionCenter.enabled) {
      return [this.optionSubAndUnsubscribed];
    }

    let defaultOptions: { value: DefaultValues; label: string }[] = [
      this.optionOnlySubscribed,
      this.optionSubAndUnsubscribed,
    ];
    if (this.isUsedInAction && this.subscriptionTopics.length < 1) {
      defaultOptions = [this.optionCampaignSetting, ...defaultOptions];
    }
    return defaultOptions;
  }

  @computed('args.model.{subscriptionTopicId,sendToUnsubscribed,hasDeletedSubscriptionTopic}')
  get selectedAudience() {
    let { sendToUnsubscribed, subscriptionTopic, hasDeletedSubscriptionTopic } = this.args.model;

    if (this.isUsedInAction) {
      return this.selectedAudienceForAction();
    }

    if (subscriptionTopic == null && sendToUnsubscribed) {
      return DefaultValues.SubAndUnsubscribed;
    } else if (hasDeletedSubscriptionTopic) {
      return null;
    } else if (subscriptionTopic == null) {
      // no default value if sub. center is enabled, explicitly selecting a topic or "send to unsubscribed" is required:
      return this.subscriptionCenter.enabled ? null : DefaultValues.OnlySubscribed;
    }

    return subscriptionTopic.id;
  }

  get showDisabledTooltip() {
    return !this.subscriptionCenter.enabled && this.isUsedInAction;
  }

  get audienceOptions() {
    if (this.subscriptionTopics.length > 0) {
      return this.optionsWithTopics.map((group) => {
        let isDisabled = group?.groupName === Audiences.TopicBased && !this.subscriptionCenter.enabled;
        return {
          ...group,
          options: group?.options?.map((opt) => {
            return {
              ...opt,
              isDisabled,
            };
          }),
          isDisabled,
        };
      });
    }
    return this.defaultOptions;
  }

  private get optionsWithTopics() {
    let topicBasedGroup = {
      groupName: Audiences.TopicBased,
      options: this.subscriptionTopics,
    };
    return [
      this.isUsedInAction ? { groupName: null, ...this.optionCampaignSetting } : null,
      topicBasedGroup,
      {
        groupName: Audiences.Global,
        options: this.defaultOptions,
      },
    ].filter(Boolean);
  }

  get subscriptionTopics() {
    if (!this.fetchSubscriptionTopics.isRunning) {
      let topics = this.fetchSubscriptionTopics?.last?.value ?? [];
      return topics.sort((a, b) => a.label.localeCompare(b.label));
    }

    return [];
  }

  get hasTopicsButIsSendingToAllWithSubscriptionCenterDisabled() {
    return (
      !this.subscriptionCenter.hasNoTopics &&
      !this.subscriptionCenter.enabled &&
      !this.args.model.sendToUnsubscribed &&
      !this.args.model.subscriptionTopic &&
      !this.isUsedInAction
    );
  }

  @computed('args.model.subscriptionTopicId')
  get modelHasTopicButSubscriptionCenterIsDisabled() {
    return this.args.model.subscriptionTopicId != null && !this.subscriptionCenter.enabled && !this.isUsedInAction;
  }

  get hasNotConfiguredSubscriptionCenter() {
    return this.tempDismissNoTopicBanner === true && this.subscriptionCenter.hasNoTopics && !this.isUsedInAction;
  }

  @computed('args.model.{sendToUnsubscribed,subscriptionTopicId}')
  get isSendingToAllSubscribedInsideAction() {
    if (!this.isUsedInAction || this.subscriptionCenter.hasNoTopics || this.subscriptionCenter.enabled) {
      return false;
    }
    let { sendToUnsubscribed, subscriptionTopicId } = this.args.model;

    // if the parent (campaign model) is sending to all subscribed and this action picks to use campaign settings
    // it's also sending to all subscribed so we show the banner
    let { campaign } = (this.args.model as unknown as { action: ActionModel }).action;
    let isUsingCampaignSettings = sendToUnsubscribed === null && subscriptionTopicId === null;

    if (isUsingCampaignSettings) {
      if (campaign.sendToUnsubscribed === false && isNone(campaign.subscriptionTopicId)) {
        return true;
      }
      return campaign.sendToUnsubscribed === null && campaign.subscriptionTopicId === null;
    }

    return subscriptionTopicId === null && sendToUnsubscribed === false;
  }

  @computed('localStorage.showNoSubTopicsBanner')
  get showNoSubTopicsBanner() {
    if (this.localStorage.get(`${SHOW_NO_TOPICS_BANNER}${this.concatEnvID}`) == null) {
      return true;
    }

    return this.localStorage.get(`${SHOW_NO_TOPICS_BANNER}${this.concatEnvID}`);
  }
  set showNoSubTopicsBanner(value) {
    this.localStorage.set(`${SHOW_NO_TOPICS_BANNER}${this.concatEnvID}`, value);
  }

  @computed('localStorage.showNoSubTopicsTimes')
  get noSubTopicsBannerViews() {
    return this.localStorage.get(`${NO_TOPICS_BANNER_VIEWS}${this.concatEnvID}`);
  }
  set noSubTopicsBannerViews(value) {
    this.localStorage.set(`${NO_TOPICS_BANNER_VIEWS}${this.concatEnvID}`, value);
  }

  @action
  private selectedAudienceForAction() {
    let { sendToUnsubscribed, subscriptionTopicId, hasDeletedSubscriptionTopic } = this.args.model;
    let isUsingCampaignSettings = sendToUnsubscribed === null && isNone(subscriptionTopicId);
    if (isUsingCampaignSettings) {
      return DefaultValues.CampaignSettings;
    }
    if (isNone(subscriptionTopicId) && sendToUnsubscribed) {
      return DefaultValues.SubAndUnsubscribed;
    } else if (isNone(subscriptionTopicId) && sendToUnsubscribed === false) {
      return DefaultValues.OnlySubscribed;
    } else if (!isNone(subscriptionTopicId)) {
      return this.subscriptionCenter.topics.find(({ id }) => id === subscriptionTopicId?.toString())?.id;
    } else if (hasDeletedSubscriptionTopic) {
      return null;
    }
    return null;
  }

  @action
  onAudienceChange({ value }: { value: DefaultValues | string; label: string }) {
    let subscriptionTopicId = null;
    if (!Object.values(DefaultValues).includes(value as any)) {
      subscriptionTopicId = value;
    }

    this.args.onChange(
      subscriptionTopicId,
      value === DefaultValues.SubAndUnsubscribed,
      value === DefaultValues.CampaignSettings,
    );
  }

  fetchSubscriptionTopics = task({ drop: true }, async () => {
    await this.subscriptionCenter.initializeTask.perform();
    return this.subscriptionCenter.topics.map((record) => ({ value: record.id, label: record.name }));
  });

  @action
  dismissNoTopicBanner() {
    this.noSubTopicsBannerViews = this.noSubTopicsBannerViews != null ? this.noSubTopicsBannerViews + 1 : 1;

    if (this.noSubTopicsBannerViews >= 1) {
      this.showNoSubTopicsBanner = false;
    }

    this.tempDismissNoTopicBanner = false;
  }
}
